/**
 * External dependencies
 */
import React, { useEffect, useCallback } from "react";
import { Translate, withLocalize } from "react-localize-redux";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

/**
 * Internal dependencies
 */
import BackButton from "../components/BackButton";
import RouteLink from "../components/RouteLink";
import { PageProps, StoreSelectedSource } from "../types";
import { analytics } from "../utils/Analytics";
import { setPageTitle } from "../utils/seo";
import { useFocusInterval } from "../utils/use-focus-interval";
import SearchInput from "../components/SearchInput";
import BarberList from "../features/barbers/BarberList";
import BarberCard, { BarberCardLoader } from "../features/barbers/BarberCard";
import { useFavoriteBarbers } from "../features/user/use-favorite-barbers";
import { useBarberSearch } from "../features/barbers/use-barber-search";
import { fetchBarbers, selectBarbers } from "../features/barbers/barbersSlice";
import ListLabel from "../features/barbershop/ListLabel";
import ApiConstants from "../constants/api";

/**
 * Assets
 */
import { ReactComponent as FavoriteIcon } from "../assets/images/icon-favorite-active.svg";
import { ReactComponent as SettingsIcon } from "../assets/images/icon-settings.svg";

/**
 * Constants
 */
const EXIT_ROUTE = "/home";
const { BARBERSHOPS_REFRESH_TIMEOUT } = ApiConstants;

interface BarbersProps extends PageProps {}

const Barbers: React.FC<BarbersProps> = (props) => {
  const { activeLanguage, translate } = props;

  /**
   * Utils, refs
   */
  const dispatch = useDispatch();

  // @TODO barberData will be replaced with actual data fetching logic, match the types then

  /**
   * Load Barbers
   */
  const barbers = useSelector(selectBarbers);

  const memoizedFetchBarbers = useCallback(() => {
    dispatch(fetchBarbers());
  }, [dispatch]);

  useEffect(() => {
    memoizedFetchBarbers();
  }, [memoizedFetchBarbers]);

  useFocusInterval(memoizedFetchBarbers, BARBERSHOPS_REFRESH_TIMEOUT);

  /**
   * Barber search
   */
  const {
    results: filteredBarbers,
    hasResults: hasfilteredBarbers,
    query,
    setQuery,
    hasQuery,
  } = useBarberSearch(barbers, 500);

  /**
   * Favorites
   */
  const favoriteBarbers = useFavoriteBarbers(barbers);

  const hasFavorites = !!(favoriteBarbers && favoriteBarbers.length);

  /**
   * SEO & Analytics
   */
  useEffect(() => {
    if (activeLanguage) {
      setPageTitle([translate("title.base"), translate("title.barbers")]);
    }
  }, [activeLanguage, translate]);

  useEffect(() => {
    analytics.pageView("/barbers", "Barbers");
  }, []);

  // const hasQuery = false; // This would be replaced with actual query state if implemented

  /**
   * Classnames
   */

  const componentClass = classNames("s-barber h-scrollable");

  return (
    <div className={componentClass}>
      <div className="s-barbers__wrapper">
        <div className="s-barbers__header">
          <div className="s-barbers__back s-barbershop__back">
            <BackButton fallbackRoute={EXIT_ROUTE} />
          </div>

          <h3 className="s-barbers__title">{translate("title.barbers")}</h3>

          <div className="s-barbers__settings">
            <RouteLink
              to="/profile/language"
              className="c-settings-link"
              transition="switch-open"
            >
              <SettingsIcon width={24} height={24} />
            </RouteLink>
          </div>
        </div>
        <div className="s-home__search">
          <SearchInput
            placeholder={translate("search.barbersPlaceholder").toString()}
            value={query}
            onChange={(value) => setQuery(value)}
            onClear={() => setQuery("")}
          />
        </div>
        {!hasQuery && hasFavorites && (
          <>
            <ListLabel icon={<FavoriteIcon />}>
              <Translate id="list.favoriteLabel" />
            </ListLabel>

            <BarberList
              className={"s-home__list s-home__list--favorites"}
              itemClassName={"s-home__item"}
              barbers={favoriteBarbers}
              render={(barber) => (
                <BarberCard
                  key={barber.id}
                  source={StoreSelectedSource.Favorite}
                  {...barber}
                />
              )}
            />
          </>
        )}

        {hasQuery && !hasfilteredBarbers && (
          <h3 className="s-home__no-results">
            <Translate id="search.resultsNotFound" />
          </h3>
        )}

        <BarberList
          className={"s-home__list s-home__list--alphabetical"}
          itemClassName={"s-home__item"}
          barbers={filteredBarbers}
          render={(barber) => (
            <BarberCard
              key={barber.id}
              source={
                hasQuery ? StoreSelectedSource.Search : StoreSelectedSource.List
              }
              {...barber}
            />
          )}
          loader={
            <>
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
              <BarberCardLoader />
            </>
          }
        />
      </div>
    </div>
  );
};

export default withLocalize(Barbers);
